import { getAlgoliaClient, getResultsIndexName } from './client';
import { AlgoliaSearchParams, AlgoliaSearchResult, AlgoliaResultRecord } from '../../types/algolia';
import { processingStatus } from '../../constants/enum';

const MAX_HITS_PER_PAGE = 1000;

/**
 * Algolia の filters 文字列で安全に使えるよう値をエスケープする
 * - " はバックスラッシュでエスケープ
 */
const escapeFilterValue = (v: string): string => v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * Algolia検索用の基本フィルタ（pattern_id, 会社, 作成者, 日付など）と
 * 完全一致モードのフィールドフィルタ（file_names, ocr_facets）を結合して filters 文字列にする
 *
 * 構文が壊れても実行時までエラーにならないため、単体テストで検証する（export はテスト用）
 */
export const buildFilters = (params: AlgoliaSearchParams): string => {
  const filterParts: string[] = [];

  // パターンID（必須）
  filterParts.push(`pattern_id:${params.patternId}`);

  if (params.companyId) {
    filterParts.push(`company_id:${params.companyId}`);
  }

  if (params.responsiblePerson) {
    filterParts.push(`user_id:${params.responsiblePerson}`);
  }

  // 担当者（#2642）
  if (params.assignee) {
    filterParts.push(`assignee_id:${params.assignee}`);
  }

  if (params.ocrStatus !== undefined && params.ocrStatus !== null) {
    filterParts.push(`ocr_status:${params.ocrStatus}`);
  }

  // Firestore 側は draft(0) と processing(1) を同じ「未処理」グループとして扱っているため
  // (api/firebase/firestore/result/read.ts 参照)、Algolia 検索でも整合させる。
  // 揃えないと OCR 完了直後のレコードが draft のまま残っていた場合に検索から除外される。
  if (params.processingStatus !== undefined && params.processingStatus !== null) {
    if (
      params.processingStatus === processingStatus.draft ||
      params.processingStatus === processingStatus.processing
    ) {
      filterParts.push(
        `(processing_status:${processingStatus.draft} OR processing_status:${processingStatus.processing})`,
      );
    } else {
      filterParts.push(`processing_status:${params.processingStatus}`);
    }
  }

  if (params.startDate) {
    filterParts.push(`created_at_timestamp >= ${params.startDate.getTime()}`);
  }
  if (params.endDate) {
    const endOfDay = new Date(params.endDate);
    endOfDay.setHours(23, 59, 59, 999);
    filterParts.push(`created_at_timestamp <= ${endOfDay.getTime()}`);
  }

  // ファイル名: 完全一致のみフィルタに変換（あいまいは query 側で扱う）
  if (params.fileName && params.fileName.trim() && params.fileNameMatchMode === 'exact') {
    filterParts.push(`file_names:"${escapeFilterValue(params.fileName.trim())}"`);
  }

  // OCR項目: 完全一致のみ filters に変換（fuzzy は query 側で扱う）
  // 選択肢項目の複数選択（#3204）は同一項目内を OR、項目間を AND で結合する
  for (const item of params.ocrItemKeywords ?? []) {
    if (item.matchMode !== 'exact') continue;
    const facets = item.keywords
      .map(keyword => keyword.trim())
      .filter(keyword => keyword !== '')
      .map(keyword => `ocr_facets:"${escapeFilterValue(`${item.fieldId}:${keyword}`)}"`);
    if (facets.length === 0) continue;
    filterParts.push(facets.length === 1 ? facets[0] : `(${facets.join(' OR ')})`);
  }

  return filterParts.join(' AND ');
};

/** 単一の Algolia 検索を全ページ取得して objectID 配列で返す */
const searchAll = async (
  query: string,
  filters: string,
  hitsPerPage: number,
  page: number | undefined,
  restrictSearchableAttributes?: string[],
): Promise<{ objectIDs: string[]; totalHits: number; totalPages: number; page: number }> => {
  const client = getAlgoliaClient();
  const indexName = getResultsIndexName();

  const buildSearchParams = (
    pageNum: number,
  ): {
    query: string;
    filters: string;
    hitsPerPage: number;
    page: number;
    attributesToRetrieve: string[];
    restrictSearchableAttributes?: string[];
  } => ({
    query,
    filters,
    hitsPerPage,
    page: pageNum,
    attributesToRetrieve: ['objectID'],
    ...(restrictSearchableAttributes && restrictSearchableAttributes.length > 0
      ? { restrictSearchableAttributes }
      : {}),
  });

  const firstResponse = await client.searchSingleIndex<AlgoliaResultRecord>({
    indexName,
    searchParams: buildSearchParams(page ?? 0),
  });

  const allObjectIDs = firstResponse.hits.map(hit => hit.objectID);
  const totalPages = firstResponse.nbPages ?? 0;

  // 2ページ目以降がある場合は追加取得（page 未指定時のみ全ページ取得）
  if (totalPages > 1 && page === undefined) {
    for (let p = 1; p < totalPages; p++) {
      const pageResponse = await client.searchSingleIndex<AlgoliaResultRecord>({
        indexName,
        searchParams: buildSearchParams(p),
      });
      allObjectIDs.push(...pageResponse.hits.map(hit => hit.objectID));
    }
  }

  return {
    objectIDs: allObjectIDs,
    totalHits: firstResponse.nbHits ?? 0,
    totalPages,
    page: firstResponse.page ?? 0,
  };
};

/** あいまい検索の対象（query / 検索対象属性） */
interface FuzzyQuery {
  keyword: string;
  attribute: 'search_text' | 'file_names';
}

/** params から実行すべき fuzzy クエリのリストを抽出する
 *
 * 注意: OCR項目の「あいまい検索」は per-field の attribute スコープを持たない。
 * Algolia には全 OCR 値が連結された search_text しか持たせていないため、
 * OCR項目の fuzzy キーワードは search_text 全体に対する検索になる。
 * 完全一致は ocr_facets で per-field を保証している。
 */
const collectFuzzyQueries = (params: AlgoliaSearchParams): FuzzyQuery[] => {
  const queries: FuzzyQuery[] = [];

  // フリーワード（search_text 全体）
  if (params.query && params.query.trim()) {
    queries.push({ keyword: params.query.trim(), attribute: 'search_text' });
  }

  // ファイル名 fuzzy → file_names 属性に限定
  if (
    params.fileName &&
    params.fileName.trim() &&
    (params.fileNameMatchMode ?? 'fuzzy') === 'fuzzy'
  ) {
    queries.push({ keyword: params.fileName.trim(), attribute: 'file_names' });
  }

  // OCR項目 fuzzy → search_text に対する検索（per-field 厳密スコープではない）
  // fuzzy になるのはテキスト・日付項目のみで検索値は常に1件。
  // 複数選択できる選択肢項目は完全一致固定のためここには来ない。
  for (const item of params.ocrItemKeywords ?? []) {
    if (item.matchMode !== 'fuzzy') continue;
    const keyword = item.keywords.map(k => k.trim()).find(k => k !== '');
    if (keyword) {
      queries.push({ keyword, attribute: 'search_text' });
    }
  }

  return queries;
};

/** 順序を保ったまま objectID リストを intersect する（最初のリストの順序を採用） */
const intersectObjectIDs = (lists: string[][]): string[] => {
  if (lists.length === 0) return [];
  if (lists.length === 1) return lists[0];

  const sets = lists.slice(1).map(list => new Set(list));
  return lists[0].filter(id => sets.every(set => set.has(id)));
};

/**
 * Algolia で複合検索を実行し、ヒットしたドキュメントIDリストを返す
 *
 * - フリーワード（search_text）と各検索項目（fileName / OCR項目）は独立に検索する
 * - matchMode が 'exact' のフィールドは Algolia の filters で完全一致検索
 * - matchMode が 'fuzzy' のフィールドは Algolia の query で typo-tolerant 検索
 * - 複数の fuzzy フィールドが指定された場合は並行検索して objectID を intersect する
 */
export const searchResults = async (params: AlgoliaSearchParams): Promise<AlgoliaSearchResult> => {
  const filters = buildFilters(params);
  const hitsPerPage = params.hitsPerPage || MAX_HITS_PER_PAGE;
  const fuzzyQueries = collectFuzzyQueries(params);

  // fuzzy が無い場合: filters のみで検索（page を渡されていれば single page、なければ全ページ）
  if (fuzzyQueries.length === 0) {
    return await searchAll('', filters, hitsPerPage, params.page);
  }

  // fuzzy が 1 件: 単一検索で完了
  if (fuzzyQueries.length === 1) {
    const q = fuzzyQueries[0];
    return await searchAll(q.keyword, filters, hitsPerPage, params.page, [q.attribute]);
  }

  // fuzzy が複数: 並行検索 → objectID を intersect
  // 順序は最初のクエリ（フリーワード優先）の relevance 順序を維持する
  const responses = await Promise.all(
    fuzzyQueries.map(q => searchAll(q.keyword, filters, hitsPerPage, undefined, [q.attribute])),
  );
  const intersected = intersectObjectIDs(responses.map(r => r.objectIDs));

  return {
    objectIDs: intersected,
    totalHits: intersected.length,
    totalPages: 1,
    page: 0,
  };
};
